import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Heart, Users, Sparkles, ScrollText } from 'lucide-react';
import { useTermsModal } from '../contexts/TermsModalContext';

// Praying-hands icon reused from the sidebar / your-prayers page.
const PrayingHands = ({ size = 28 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M11 3 C 9.5 5, 7.5 9, 7 14 C 6.7 17, 8 19, 9.5 20 L 11.5 20 Z" />
    <path d="M13 3 C 14.5 5, 16.5 9, 17 14 C 17.3 17, 16 19, 14.5 20 L 12.5 20 Z" />
    <line x1="12" y1="3.5" x2="12" y2="20" />
  </svg>
);

const About = () => {
  const { openTerms } = useTermsModal();
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="about-page">
      {/* Hero */}
      <div className="rounded-2xl bg-gradient-to-br from-amber-100 via-rose-100 to-violet-100 dark:from-amber-950/40 dark:via-rose-950/40 dark:to-violet-950/40 p-8 sm:p-10 mb-8 text-center">
        <div className="mx-auto w-16 h-16 rounded-full bg-white/70 dark:bg-white/10 backdrop-blur flex items-center justify-center mb-4 text-amber-600 dark:text-amber-300">
          <PrayingHands size={30} />
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-zinc-900 dark:text-white mb-2" data-testid="about-title">
          About Pray Now Together
        </h1>
        <p className="text-sm sm:text-base text-zinc-700 dark:text-zinc-300 max-w-xl mx-auto">
          A place to share what&apos;s on your heart, and join in praying for others.
        </p>
      </div>

      {/* Story */}
      <section className="space-y-4 mb-10" data-testid="about-story">
        <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">Our Story</h2>
        <p className="text-[15px] leading-relaxed text-zinc-700 dark:text-zinc-300">
          Pray Now Together started with a simple idea: praying with others is powerful. And, in a world that moves quickly, it can be hard to find people who&apos;ll
          pause with you — even for a moment — to lift up what matters most.
        </p>
        <p className="text-[15px] leading-relaxed text-zinc-700 dark:text-zinc-300">
          We built this space so that anyone, anywhere, can share a link
          to their prayer via email, text or social media as an invitation
          to pray alongside them. Every candle lit
          is a quiet promise that someone, somewhere, is standing with you.
        </p>
        <p className="text-[15px] leading-relaxed text-zinc-700 dark:text-zinc-300">
          Whether you&apos;re asking for strength, healing, offering thanks, thinking of friends,
          family or a stranger, we hope this becomes a place you return to and pray for each other.
        </p>
      </section>

      {/* Values */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10" data-testid="about-values">
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 p-5">
          <Heart size={22} className="text-rose-500 mb-2" />
          <div className="font-semibold text-zinc-900 dark:text-white mb-1">Prayed for</div>
          <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
            Every prayer shared is open to people who care and respond.
          </p>
        </div>
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 p-5">
          <Users size={22} className="text-amber-500 mb-2" />
          <div className="font-semibold text-zinc-900 dark:text-white mb-1">Together</div>
          <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
            Join Circles to stay close to the people whose prayers are important to you.
          </p>
        </div>
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 p-5">
          <Sparkles size={22} className="text-violet-500 mb-2" />
          <div className="font-semibold text-zinc-900 dark:text-white mb-1">Assistance</div>
          <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
            If you would like help writing a prayer, use the built in assistance tool to get you started.
          </p>
        </div>
      </section>

      {/* Legal */}
      <section className="rounded-xl border border-zinc-200 dark:border-zinc-800 p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <div className="font-semibold text-zinc-900 dark:text-white mb-0.5">Legal</div>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Review our Terms and Conditions to understand the guidelines, rights, and responsibilities for using our prayer sanctuary.
          </p>
        </div>
        <button
          type="button"
          onClick={openTerms}
          data-testid="about-legal-btn"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 hover:opacity-90 text-sm font-medium whitespace-nowrap"
        >
          <ScrollText size={16} />
          Terms & Conditions
        </button>
      </section>

      <div className="text-center text-xs text-zinc-500 pb-6 space-y-1">
        <div>© {new Date().getFullYear()} Pray Now Together</div>
        <button
          type="button"
          onClick={openTerms}
          data-testid="about-terms-link"
          className="text-amber-600 dark:text-amber-400 hover:underline underline-offset-2 font-medium bg-transparent border-0 p-0 cursor-pointer"
        >
          Terms &amp; Conditions
        </button>
      </div>

    </div>
  );
};

export default About;
